const url = process.env.REACT_APP_SUPABASE_URL;
const anonKey = process.env.REACT_APP_SUPABASE_ANON;

const callEdgeFunction = async (functionName, body = {}) => {
  const response = await fetch(`${url}/functions/v1/${functionName}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${anonKey}`,
    },
    body: JSON.stringify(body),
  });
  return response.json();
};

export const fetchMasterAccessData = async (username) => {
  username = username.toLowerCase();

  const result = await callEdgeFunction("fetch-master-access-by-username", {
    username,
  });

  if (result.error) {
    return { error: result.error };
  }

  const data = result.data;
  if (
    !data ||
    !data[0] ||
    !data[0].organization_name ||
    !data[0].contact_link ||
    !data[0].image_url
  ) {
    return { error: "Required data not found or incomplete." };
  }

  return {
    organization_name: data[0].organization_name,
    contact_link: data[0].contact_link,
    image_url: data[0].image_url,
    first_login: data[0].first_login,
    incentive_column_title: data[0].incentive_column_title,
  };
};

/** In-flight + success cache so Navbar and Master dashboard share one Supabase call per code. */
const masterAccessByCodeInFlight = new Map();
const masterAccessByCodeCache = new Map();

export const clearMasterAccessByCodeCache = () => {
  masterAccessByCodeInFlight.clear();
  masterAccessByCodeCache.clear();
};

const normalizeMasterAccessByCodeResponse = (result) => {
  if (result.error) {
    return { error: result.error };
  }

  const data = result.data;
  if (
    !data ||
    !data.cityAccess ||
    !data.cityAccess.organization_name ||
    !data.cityAccess.contact_link ||
    !data.cityAccess.image_url
  ) {
    return { error: "Required data not found or incomplete." };
  }

  return {
    cityAccess: {
      organization_name: data.cityAccess.organization_name,
      contact_link: data.cityAccess.contact_link,
      image_url: data.cityAccess.image_url,
      first_login: data.cityAccess.first_login,
      incentive_column_title: data.cityAccess.incentive_column_title,
      program_name: data.cityAccess.program_name,
    },
    cityAccessList: Array.isArray(data.cityAccessList)
      ? data.cityAccessList
      : [],
  };
};

/**
 * Fetches master access by code (single query). Returns org metadata and the list of
 * user links for that org.
 * Deduplicates concurrent requests and reuses successful responses for the same code.
 */
export const fetchMasterAccessByCode = async (access_code) => {
  const key = access_code == null ? "" : String(access_code);
  const cached = masterAccessByCodeCache.get(key);
  if (cached) {
    return cached;
  }
  const pending = masterAccessByCodeInFlight.get(key);
  if (pending) {
    return pending;
  }

  const promise = (async () => {
    const result = await callEdgeFunction("fetch-master-access-by-code", {
      access_code,
    });
    const normalized = normalizeMasterAccessByCodeResponse(result);
    if (!normalized.error) {
      masterAccessByCodeCache.set(key, normalized);
    }
    return normalized;
  })();

  masterAccessByCodeInFlight.set(key, promise);
  promise.finally(() => {
    masterAccessByCodeInFlight.delete(key);
  });

  return promise;
};

/**
 * Generates a fresh magic link for a city_access user (same org as access_code).
 * Call on each Access click so links are not expired and the tab open stays user-gesture safe.
 */
export const generateMasterAccessUserMagicLink = async (
  access_code,
  username,
) => {
  if (!access_code || !username) {
    return { error: "access_code and username are required" };
  }

  const result = await callEdgeFunction("generate-master-access-magic-link", {
    access_code,
    username: String(username).toLowerCase().trim(),
  });

  if (result.error) {
    return {
      error:
        typeof result.error === "string"
          ? result.error
          : result.error?.message || "Request failed",
    };
  }

  const link = result.data?.link;
  if (!link) {
    return { error: "Could not generate access link" };
  }

  return { link };
};

export const fetchFleetDataByUserIds = async (userIds) => {
  if (!Array.isArray(userIds) || userIds.length === 0) {
    return { data: [] };
  }
  const uniqueIds = [...new Set(userIds)];
  const result = await callEdgeFunction("fetch-fleet-data-by-user-ids", {
    userIds: uniqueIds,
  });
  if (result.error) {
    return { error: result.error };
  }
  return { data: result.data ?? [] };
};

export const fetchSiteLocationsByUserIds = async (userIds) => {
  if (!Array.isArray(userIds) || userIds.length === 0) {
    return { data: [] };
  }
  const uniqueIds = [...new Set(userIds)];
  const result = await callEdgeFunction("fetch-site-locations-by-user-ids", {
    userIds: uniqueIds,
  });
  if (result.error) {
    return { error: result.error };
  }
  return { data: result.data ?? [] };
};

export const fetchPhasesByUserIds = async (userIds) => {
  if (!Array.isArray(userIds) || userIds.length === 0) {
    return { data: [] };
  }

  const result = await callEdgeFunction("fetch-phases-by-user-ids", {
    userIds,
  });

  if (result.error) {
    return { error: result.error };
  }

  return { data: result.data ?? [] };
};

export const fetchControlsByUserIds = async (userIds) => {
  if (!Array.isArray(userIds) || userIds.length === 0) {
    return { data: [] };
  }
  const result = await callEdgeFunction("fetch-controls-by-user-ids", {
    userIds,
  });
  if (result.error) return { error: result.error };
  return { data: result.data ?? [] };
};

export const fetchAdvancedControlsByUserIds = async (userIds) => {
  if (!Array.isArray(userIds) || userIds.length === 0) {
    return { data: [] };
  }
  const result = await callEdgeFunction("fetch-advanced-controls-by-user-ids", {
    userIds,
  });
  if (result.error) return { error: result.error };
  return { data: result.data ?? [] };
};

export const fetchDefaultDataByUserIds = async (userIds) => {
  if (!Array.isArray(userIds) || userIds.length === 0) {
    return { data: [] };
  }
  const result = await callEdgeFunction("fetch-default-data-by-user-ids", {
    userIds,
  });
  if (result.error) return { error: result.error };
  return { data: result.data ?? [] };
};

export const fetchCityInfoByUserIds = async (userIds) => {
  if (!Array.isArray(userIds) || userIds.length === 0) {
    return { data: [] };
  }
  const result = await callEdgeFunction("fetch-city-info-by-user-ids", {
    userIds,
  });
  if (result.error) return { error: result.error };
  return { data: result.data ?? [] };
};

export const fetchChargerCostsByUserIds = async (userIds) => {
  if (!Array.isArray(userIds) || userIds.length === 0) {
    return { data: [] };
  }
  const result = await callEdgeFunction("fetch-charger-costs-by-user-ids", {
    userIds,
  });
  if (result.error) return { error: result.error };
  return { data: result.data ?? [] };
};

export const updateFirstLogin = async (access_code) => {
  const result = await callEdgeFunction("update-first-login", {
    access_code,
  });

  if (result.error) {
    return { error: result.error };
  }

  const data = result.data;
  if (!data || data.length === 0) {
    return { error: "Access code not found" };
  }

  return {
    message: "Successfully updated first login status",
    data: data[0],
  };
};

export const fetchAllOrganizations = async () => {
  const result = await callEdgeFunction("fetch-all-organizations", {});

  if (result.error) {
    return { error: result.error };
  }

  return { data: result.data ?? [] };
};

export const fetchCityAccessForOrganization = async (organizationName) => {
  if (!organizationName) {
    return { error: "Organization name is required" };
  }

  const result = await callEdgeFunction("fetch-city-access-for-organization", {
    organizationName,
  });

  if (result.error) {
    return { error: result.error };
  }

  if (!result.data) {
    return { error: "Organization not found" };
  }

  return {
    data: result.data.city_access || [],
  };
};

export const addUsernameToCityAccess = async (organizationName, username) => {
  if (!organizationName || !username) {
    return { error: "Organization name and username are required" };
  }

  const normalizedUsername = username.toLowerCase().trim();

  const fetchResult = await callEdgeFunction(
    "fetch-city-access-for-organization",
    { organizationName },
  );

  if (fetchResult.error) {
    return { error: fetchResult.error };
  }

  if (!fetchResult.data) {
    return { error: "Organization not found" };
  }

  const currentCityAccess = fetchResult.data.city_access || [];
  if (currentCityAccess.includes(normalizedUsername)) {
    return {
      error: "Username already exists in city_access for this organization",
    };
  }

  const updatedCityAccess = [...currentCityAccess, normalizedUsername];

  const updateResult = await callEdgeFunction("update-city-access", {
    organizationName,
    city_access: updatedCityAccess,
  });

  if (updateResult.error) {
    return { error: updateResult.error };
  }

  const data = updateResult.data;
  if (!data || data.length === 0) {
    return { error: "Failed to update city_access" };
  }

  return {
    message: "Successfully added username to city_access",
    data: data[0],
  };
};

export const removeUsernameFromCityAccess = async (
  organizationName,
  username,
) => {
  if (!organizationName || !username) {
    return { error: "Organization name and username are required" };
  }

  const normalizedUsername = username.toLowerCase().trim();

  const fetchResult = await callEdgeFunction(
    "fetch-city-access-for-organization",
    { organizationName },
  );

  if (fetchResult.error) {
    return { error: fetchResult.error };
  }

  if (!fetchResult.data) {
    return { error: "Organization not found" };
  }

  const currentCityAccess = fetchResult.data.city_access || [];

  if (!currentCityAccess.includes(normalizedUsername)) {
    return {
      error: "Username does not exist in city_access for this organization",
    };
  }

  const updatedCityAccess = currentCityAccess.filter(
    (user) => user !== normalizedUsername,
  );

  const updateResult = await callEdgeFunction("update-city-access", {
    organizationName,
    city_access: updatedCityAccess,
  });

  if (updateResult.error) {
    return { error: updateResult.error };
  }

  const data = updateResult.data;
  if (!data || data.length === 0) {
    return { error: "Failed to update city_access" };
  }

  return {
    message: "Successfully removed username from city_access",
    data: data[0],
  };
};
